import { useEffect, useRef } from 'react';
import { useEffectDeep } from '../../../hooks/useEffectDeep';
import { Feature } from 'geojson';
import { createPathsFromGeoJSON } from '../utils';
import { useMap } from '../hooks';

export interface PolygonProps {
    onClick?: (event: google.maps.MapMouseEvent) => void;
    onMouseOver?: (event: google.maps.MapMouseEvent) => void;
    onMouseOut?: (event: google.maps.MapMouseEvent) => void;

    paths?:
        | google.maps.MVCArray<google.maps.LatLng>
        | google.maps.MVCArray<google.maps.MVCArray<google.maps.LatLng>>
        | google.maps.LatLng[]
        | google.maps.LatLng[][]
        | google.maps.LatLngLiteral[]
        | google.maps.LatLngLiteral[][];

    geometry?: Feature;

    options?: google.maps.PolygonOptions;
}

export const Polygon: React.VFC<PolygonProps> = ({
    onClick,
    onMouseOut,
    onMouseOver,
    paths,
    geometry,
    options,
}: PolygonProps) => {
    const polygon = useRef(new google.maps.Polygon());
    const { map } = useMap();

    useEffect(() => {
        polygon.current.setMap(map);

        if (onClick) {
            polygon.current.addListener('click', onClick);
        }

        if (onMouseOver) {
            polygon.current.addListener('mouseover', onMouseOver);
        }

        if (onMouseOut) {
            polygon.current.addListener('mouseout', onMouseOut);
        }

        polygon.current.setOptions(options || null);
        if (paths) {
            polygon.current.setPaths(paths);
        } else if (geometry) {
            polygon.current.setPaths(createPathsFromGeoJSON(geometry));
        }
    }, [map]);

    useEffect(() => {
        return () => {
            polygon.current.setMap(null);
        };
    }, []);

    useEffectDeep(() => {
        polygon.current.setOptions(options || null);
    }, [options]);

    useEffectDeep(() => {
        if (paths) {
            polygon.current.setPaths(paths);
        }
    }, [paths]);

    useEffectDeep(() => {
        if (!paths && geometry) {
            polygon.current.setPaths(createPathsFromGeoJSON(geometry));
        }
    }, [paths, geometry]);

    return null;
};
